// The workout ENERGY READOUT bundle (HC-8). One derivation of a session's
// energy-view inputs — the read-resolved calories (estimate-vs-measured), the
// measured-value origin, the session HR, and %HRmax — so every surface that
// shows <CaloriesEffortBlock> (mobile logger footer + post-save card, web-rnw
// session detail) computes it ONCE here instead of hand-assembling the same
// five props. Pure, IO-free.
//
// The bundle's field names line up 1:1 with CaloriesEffortBlockProps (minus
// theme/showSources), so a caller spreads it straight in: `{...readout}`.
//
// Age is passed in (via `ageFromBucket` at the callsite) — Profile stores only
// an AgeBucket, and keeping the source out of here mirrors hrEffort.ts's
// age-source-agnostic shape.

import { percentHrMax } from "./hrEffort.js";
import {
  resolveWorkoutCalories,
  type ResolvedWorkoutCalories,
} from "./resolveWorkoutCalories.js";
import type { CaloriesEstimate } from "./schemas/session.js";
import type { WorkoutExerciseEntry } from "./schemas/workout.js";

export type WorkoutEnergyReadout = {
  /** Read-resolved canonical calories (null when the session carries none). */
  resolved: ResolvedWorkoutCalories | null;
  /** HC dataOrigin of the measured value — resolves its source name. */
  externalOrigin?: string;
  avgHrBpm?: number;
  peakHrBpm?: number;
  /** #156: total distance of the cardio sets, in metres; absent when none. */
  distanceM?: number;
  /** avg-HR as %HRmax, or null when HR/age missing. */
  hrMaxPct: number | null;
  /** LG-46 — the live count includes a set the athlete hasn't done yet, so the
   *  reading is a projection of the whole session, not a measurement of work
   *  already logged. Only the live path sets it; a saved session leaves it off.
   *  The (i) provenance uses it to declare the projection (the number itself
   *  reads clean, per the no-inline-source-label rule). */
  projected?: boolean;
  /** LEGAL-5 ripiego (2026-09-09): MET-only twin of `resolved.estimate`, HR
   *  stripped (calorieEstimate.ts's OWN MET route, not a second engine) — the
   *  share card's fallback when the canonical figure touches Health Connect
   *  (measured, or a Keytel estimate). Set only by callers that hold the raw
   *  per-set ingredients to recompute it (the live draft; a saved session's
   *  assembled Workout) — absent on a thin Session-envelope-only caller (e.g.
   *  web's session-detail view), where the ripiego cell then stays omitted. */
  metFallbackKcal?: number | null;
};

/** The energy-bearing fields a session (or an assembled Workout carrying them
 *  read-only) exposes. */
export type EnergyBearingSession = {
  calories?: number;
  caloriesEstimate?: CaloriesEstimate;
  avgHrBpm?: number;
  peakHrBpm?: number;
  externalOrigin?: string;
  /** The sets, when the caller holds them (an assembled Workout does, the thin
   *  Session envelope does not). #156: an imported cardio session keeps its
   *  heart rate and distance on the cardio set, so the readout reads them
   *  from here when the session itself carries none. */
  entries?: readonly Pick<WorkoutExerciseEntry, "sets">[];
};

/** Heart rate and distance of a session's cardio sets (#156). Read-only: it
 *  derives from the sets as stored, nothing is written back. The average is
 *  weighted by set duration; a set with no heart rate does not dilute it. */
export function cardioSetTotals(
  entries: EnergyBearingSession["entries"],
): { avgHrBpm?: number; distanceM?: number } {
  let hrWeighted = 0;
  let hrSeconds = 0;
  let distanceM = 0;
  for (const entry of entries ?? []) {
    for (const set of entry.sets) {
      if (set.type !== "cardio") continue;
      if (set.avgHrBpm !== undefined) {
        hrWeighted += set.avgHrBpm * set.duration;
        hrSeconds += set.duration;
      }
      distanceM += set.distanceM ?? 0;
    }
  }
  return {
    ...(hrSeconds > 0 ? { avgHrBpm: Math.round(hrWeighted / hrSeconds) } : {}),
    ...(distanceM > 0 ? { distanceM } : {}),
  };
}

/** Derive the full energy readout from a session's stored fields. `hcWins` is
 *  the caller's preference (default-ON on the client toggle; web pins ON);
 *  `ageYears` comes from `ageFromBucket(profile.ageBucket)`. */
export function sessionEnergyReadout(
  session: EnergyBearingSession,
  opts: { hcWins: boolean; ageYears: number | undefined },
): WorkoutEnergyReadout {
  const fromSets = cardioSetTotals(session.entries);
  const avgHrBpm = session.avgHrBpm ?? fromSets.avgHrBpm;
  return {
    resolved: resolveWorkoutCalories(session, { hcWins: opts.hcWins }),
    externalOrigin: session.externalOrigin,
    avgHrBpm,
    peakHrBpm: session.peakHrBpm,
    ...(fromSets.distanceM !== undefined ? { distanceM: fromSets.distanceM } : {}),
    hrMaxPct: percentHrMax(avgHrBpm, opts.ageYears),
  };
}
